(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let motionEnabled = root.dataset.motion === 'true' && !reduceMotion.matches;
  let viewportFrame;
  let scrollFrame;

  root.classList.remove('no-js');
  root.classList.add('js');
  root.dataset.motionEnabled = motionEnabled ? 'true' : 'false';

  const announce = (message) => {
    const region = document.getElementById('FumidarLiveRegion');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 80);
  };

  const setViewportHeight = () => {
    window.cancelAnimationFrame(viewportFrame);
    viewportFrame = window.requestAnimationFrame(() => {
      root.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    });
  };

  const updateScrolledState = () => {
    window.cancelAnimationFrame(scrollFrame);
    scrollFrame = window.requestAnimationFrame(() => {
      const scrolled = window.scrollY > 12;
      root.classList.toggle('is-scrolled', scrolled);
      document.querySelectorAll('[data-site-header]').forEach((header) => {
        header.classList.toggle('is-scrolled', scrolled);
      });
    });
  };

  const readVariants = (section) => {
    const source = section.querySelector('[data-product-variants]');
    if (!source) return [];
    try {
      return JSON.parse(source.textContent);
    } catch {
      return [];
    }
  };

  const initializeGallery = (section) => {
    const gallery = section.querySelector('[data-product-gallery]');
    if (!gallery || section.fumidarGallery) return;
    const items = [...gallery.querySelectorAll('[data-media-id]')];
    const thumbnails = [...gallery.querySelectorAll('[data-gallery-thumbnail]')];
    const stage = gallery.querySelector('[data-gallery-stage]');
    const controls = gallery.querySelector('[data-gallery-controls]');
    const thumbnailList = gallery.querySelector('[data-gallery-thumbnails]');
    const count = gallery.querySelector('[data-gallery-count]');
    const empty = gallery.querySelector('[data-gallery-empty]');
    const dialog = gallery.querySelector('[data-gallery-dialog]');
    let visibleItems = [];
    let activeIndex = 0;
    let currentColor;
    let touchStart;
    let lastSwipe = 0;

    const activate = (index) => {
      activeIndex = visibleItems.length ? (index + visibleItems.length) % visibleItems.length : 0;
      const active = visibleItems[activeIndex];
      items.forEach((item) => { item.hidden = item !== active; });
      thumbnails.forEach((thumbnail) => {
        thumbnail.setAttribute('aria-pressed', String(thumbnail.dataset.galleryThumbnail === active?.dataset.mediaId));
      });
      const image = active?.querySelector('img');
      if (image) image.loading = 'eager';
      if (count) count.textContent = visibleItems.length ? `${activeIndex + 1} / ${visibleItems.length}` : '';
      if (empty) empty.hidden = visibleItems.length > 0;
    };

    const selectColor = (color, featuredImageId) => {
      if (currentColor === color) return;
      currentColor = color;
      visibleItems = items.filter((item) => {
        const colors = (item.dataset.mediaColors || '').split('|').filter(Boolean);
        return !color || colors.length === 0 || colors.includes(color);
      });
      const visibleIds = new Set(visibleItems.map((item) => item.dataset.mediaId));
      thumbnails.forEach((thumbnail) => { thumbnail.hidden = !visibleIds.has(thumbnail.dataset.galleryThumbnail); });
      if (controls) controls.hidden = visibleItems.length < 2;
      if (thumbnailList) thumbnailList.hidden = visibleItems.length < 2;
      const featuredIndex = visibleItems.findIndex((item) => item.dataset.mediaId === String(featuredImageId));
      activate(featuredIndex < 0 ? 0 : featuredIndex);
      if (thumbnailList) thumbnailList.scrollLeft = 0;
    };

    gallery.querySelector('[data-gallery-previous]')?.addEventListener('click', () => activate(activeIndex - 1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => activate(activeIndex + 1));
    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        const index = visibleItems.findIndex((item) => item.dataset.mediaId === thumbnail.dataset.galleryThumbnail);
        if (index >= 0) activate(index);
      });
    });
    gallery.addEventListener('keydown', (event) => {
      if (dialog?.open || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      activate(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });
    stage?.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
    }, { passive: true });
    stage?.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touchStart || !touch) return;
      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;
      if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3) {
        activate(activeIndex + (deltaX < 0 ? 1 : -1));
        lastSwipe = Date.now();
      }
      touchStart = null;
    }, { passive: true });
    gallery.querySelectorAll('[data-gallery-zoom]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (Date.now() - lastSwipe < 400) { event.preventDefault(); return; }
        if (typeof dialog?.showModal !== 'function') return;
        event.preventDefault();
        const fullImage = dialog.querySelector('[data-gallery-full-image]');
        fullImage.src = link.href;
        fullImage.alt = link.querySelector('img')?.alt || '';
        dialog.showModal();
      });
    });
    gallery.querySelector('[data-gallery-close]')?.addEventListener('click', () => dialog.close());
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    section.fumidarGallery = { selectColor };
  };

  const renderVariant = (section, variant, notify = true) => {
    const price = section.querySelector('[data-price-wrapper]');
    const currentPrice = price?.querySelector('[data-price-current]');
    const compareAt = price?.querySelector('[data-price-compare]');
    const priceLabel = price?.querySelector('[data-price-label]');
    const addButton = section.querySelector('[data-add-to-cart]');
    const variantInput = section.querySelector('[data-variant-input]');
    const selection = section.querySelector('[data-variant-selection]');

    const color = section.querySelector('[data-color-option] [data-option-value][aria-pressed="true"]')?.dataset.colorHandle || '';
    section.fumidarGallery?.selectColor(color, variant?.featuredImageId);
    if (variantInput) {
      variantInput.disabled = !variant;
      variantInput.value = variant ? String(variant.id) : '';
    }
    if (!variant) {
      if (addButton) { addButton.disabled = true; addButton.textContent = addButton.dataset.unavailableLabel; }
      if (selection) selection.textContent = selection.dataset.unavailableLabel;
      section.querySelectorAll('.shopify-payment-button').forEach((button) => { button.hidden = true; });
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      window.history.replaceState({}, '', url);
      if (notify) announce(addButton?.dataset.unavailableLabel);
      return;
    }

    if (currentPrice) currentPrice.textContent = variant.price;
    if (compareAt) {
      compareAt.textContent = variant.compareAt;
      compareAt.hidden = !variant.onSale;
    }
    if (price) price.classList.toggle('price--sale', variant.onSale);
    if (priceLabel) {
      priceLabel.textContent = variant.onSale ? priceLabel.dataset.saleLabel : priceLabel.dataset.regularLabel;
    }
    if (addButton) {
      addButton.disabled = !variant.available;
      addButton.textContent = variant.available ? addButton.dataset.addLabel : addButton.dataset.soldOutLabel;
    }
    if (variantInput) variantInput.value = String(variant.id);
    if (selection) selection.textContent = selection.dataset.selectionLabel.replace('[variant]', variant.title);

    section.querySelectorAll('.shopify-payment-button').forEach((button) => {
      button.hidden = !variant.available;
    });

    if (notify) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
      announce(`${variant.title} - ${variant.available ? addButton?.dataset.addLabel : addButton?.dataset.soldOutLabel}`);
    }
  };

  const variantForOptions = (variants, options) => variants.find((variant) => (
    variant.options.length === options.length
      && variant.options.every((value, index) => value === options[index])
  ));

  const updateOptionAvailability = (section, variants, selectedOptions) => {
    section.querySelectorAll('[data-product-option]').forEach((group) => {
      const position = Number(group.dataset.optionPosition) - 1;
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        const candidateOptions = [...selectedOptions];
        candidateOptions[position] = button.dataset.optionValue;
        const candidate = variantForOptions(variants, candidateOptions);
        button.classList.toggle('is-unavailable', !candidate?.available);
      });
    });
  };

  const initializeProductSections = (scope = document) => {
    const sections = [...scope.querySelectorAll('[data-product-section]')];
    if (scope instanceof Element && scope.matches('[data-product-section]')) sections.unshift(scope);
    sections.forEach((section) => {
      if (section.dataset.productInitialized === 'true') return;
      initializeGallery(section);
      const optionGroups = [...section.querySelectorAll('[data-product-option]')];
      const variants = readVariants(section);
      if (optionGroups.length === 0 || variants.length === 0) {
        section.fumidarGallery?.selectColor('', variants[0]?.featuredImageId);
        section.dataset.productInitialized = 'true';
        return;
      }

      section.dataset.productInitialized = 'true';
      const selectedOptions = optionGroups.map((group) => (
        group.querySelector('[data-option-value][aria-pressed="true"]')?.dataset.optionValue
      ));
      updateOptionAvailability(section, variants, selectedOptions);
      renderVariant(section, variantForOptions(variants, selectedOptions), false);

      optionGroups.forEach((group) => {
        group.addEventListener('click', (event) => {
          const button = event.target.closest('[data-option-value]');
          if (!(button instanceof HTMLButtonElement)) return;
          const position = Number(group.dataset.optionPosition) - 1;
          selectedOptions[position] = button.dataset.optionValue;
          group.querySelectorAll('[data-option-value]').forEach((choice) => {
            choice.setAttribute('aria-pressed', String(choice === button));
          });
          const selectedLabel = group.querySelector('[data-selected-option]');
          if (selectedLabel) selectedLabel.textContent = button.dataset.optionLabel || button.dataset.optionValue;
          updateOptionAvailability(section, variants, selectedOptions);
          const variant = variantForOptions(variants, selectedOptions);
          renderVariant(section, variant);
        });
      });
    });
  };

  const initializeProductCards = (scope = document) => {
    scope.querySelectorAll('.product-card').forEach((card) => {
      if (card.dataset.swatchesInitialized === 'true') return;
      const swatches = card.querySelectorAll('[data-card-swatch]');
      if (swatches.length === 0) return;
      card.dataset.swatchesInitialized = 'true';
      swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          const image = card.querySelector('.product-card__image:not(.product-card__image--secondary)');
          card.querySelectorAll('[data-product-card-link]').forEach((link) => {
            link.href = swatch.dataset.variantUrl;
          });
          card.querySelectorAll('[data-price-current]').forEach((price) => {
            price.textContent = swatch.dataset.variantPrice;
          });
          if (image && swatch.dataset.imageSrc) {
            image.src = swatch.dataset.imageSrc;
            image.srcset = swatch.dataset.imageSrcset;
            image.alt = swatch.dataset.imageAlt;
          }
          swatches.forEach((choice) => {
            const selected = choice === swatch;
            choice.classList.toggle('is-selected', selected);
            choice.setAttribute('aria-pressed', String(selected));
          });
          card.classList.add('is-color-selected');
          announce(swatch.getAttribute('aria-label'));
        });
      });
    });
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    document.querySelectorAll('details[open][data-close-outside]').forEach((details) => {
      if (!details.contains(target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[open]').forEach((details) => {
      details.removeAttribute('open');
    });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('.fumidar-localization form')) {
      const returnTo = form.querySelector('input[name="return_to"]');
      if (returnTo instanceof HTMLInputElement) {
        returnTo.value = `/${window.location.pathname.replace(/^\/+/, '')}${window.location.search}`;
      }
    }
    const submitter = event.submitter;
    if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
      submitter.setAttribute('aria-busy', 'true');
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) {
      initializeProductSections(event.target);
      initializeProductCards(event.target);
    }
  });

  reduceMotion.addEventListener('change', () => {
    motionEnabled = root.dataset.motion === 'true' && !reduceMotion.matches;
    root.dataset.motionEnabled = motionEnabled ? 'true' : 'false';
  });

  window.addEventListener('resize', setViewportHeight, { passive: true });
  window.addEventListener('scroll', updateScrolledState, { passive: true });

  setViewportHeight();
  updateScrolledState();
  initializeProductSections();
  initializeProductCards();

  window.Fumidar = Object.freeze({ announce, initializeProductSections, initializeProductCards });
})();
